import { Injectable } from '@angular/core';

import { SelectedSection } from 'app/modules/widget/components/section-selector/section-selector.component';
import { StorageAPIService } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';

import { FilterUtils } from 'app/utils/filter.utils';
import { BodyParams } from '../api.service';
import { WidgetDataSource } from '../../models';
import { urlFor } from './utils';

export interface Report<T = { [key: string]: any }> {
  generatedAt: Date;
  totalHits: number;
  items: T;
}

export interface NumericFieldStatistics {
  min: number;
  max: number;
  sum: number;
  avg: number;
  missing: number;
  count: number;
}

export interface DateFieldStatistics {
  min: number;
  max: number;
  missing: number;
  count: number;
}

export interface StringFieldStatistics {
  missing: number;
  cardinality: number;
  mostCommon: [{ [key: string]: number }];
}

export interface FieldStatisticsReport {
  standard: {
    [key: string]: NumericFieldStatistics | DateFieldStatistics | StringFieldStatistics;
  };
  custom: {
    [key: string]: NumericFieldStatistics | DateFieldStatistics | StringFieldStatistics;
  };
}

export interface TaxonomyReport {
  nodes: { [key: string]: TaxonomyNode };
}

export interface TaxonomyNode {
  taxonomyFullPath: string;
  id: string;
  name: string;
  path: string;
  docCount: number;
  leaf: boolean;
  children?: TaxonomyNode[];
}

interface TaxonomyNodesReportParams {
  analysisName: string;
  featureName: string;
  isSocial: boolean;
  timezone: string;
  featureSectionFilter?: string[];
}

function transformFeatureType(featureType: string): string {
  if (featureType.startsWith('sectionProperties.')) {
    return featureType.replace('sectionProperties.', 'flatSections.');
  }
  return featureType;
}

export function encodeParams(params: { [key: string]: any }, analysisGroupId): string {
  const paramsParts = [];

  if (params.excludedValues) {
    params.excludedValues.forEach((feature) => {
      paramsParts.push(`excludedFeatures=${encodeURIComponent(feature.name)}`);
      paramsParts.push(
        `excludedFeatureTypes=${encodeURIComponent(transformFeatureType(feature.type))}`
      );
      if (feature.subType && feature.type !== 'taxonomy') {
        paramsParts[paramsParts.length - 1] += encodeURIComponent(`:${feature.subType}`);
      }
    });

    delete params.excludedValues;
  }

  if (params.selectedItems) {
    params.selectedItems.forEach((feature) => {
      paramsParts.push('features=' + encodeURIComponent(feature));
    });

    delete params.selectedItems;
  }

  if (params.featureValue !== undefined) {
    paramsParts.push('featureValue=' + encodeURIComponent(params.featureValue));

    delete params.featureValue;
  }

  for (const key of Object.getOwnPropertyNames(params)) {
    if (key === 'analysisName' && analysisGroupId) {
      continue;
    }
    const value = params[key];
    if (value !== '' && value !== null && typeof value !== 'undefined') {
      paramsParts.push(key + '=' + encodeURIComponent(value));
    }
  }
  return paramsParts.join('&');
}

export function remapParamsAsObject(params: { [key: string]: any }) {
  if (params.excludedValues) {
    params.excludedFeatures = [];
    params.excludedFeatureTypes = [];
    params.excludedValues.forEach((feature) => {
      params.excludedFeatures.push(feature.name);
      params.excludedFeatureTypes.push(transformFeatureType(feature.type));
    });
    delete params.excludedValues;
  }

  if (params.selectedItems) {
    params.features = [];
    params.selectedItems.forEach((feature) => params.features.push(feature));
    delete params.selectedItems;
  }
  return params;
}

@EnableClassTelemetry()
@Injectable()
export class ReportService extends StorageAPIService {
  getReportPermalink(
    collectionName: string,
    analysisGroupId: string,
    reportName: string,
    params: { [key: string]: any },
    query?: string,
    filter?: string,
    cache?: string,
    format: 'json' | 'csv' = 'json',
    projectId?: string,
    skipParams = false
  ): string {
    const authToken = this.getToken();
    const urlParts = [`token=${authToken}`];

    if (!skipParams) {
      const paramsString = encodeParams(params, analysisGroupId);
      if (paramsString) {
        urlParts.push('params=' + encodeURIComponent(paramsString));
      }

      if (query) {
        urlParts.push('query=' + encodeURIComponent(query));
      }

      if (filter) {
        urlParts.push('filter=' + encodeURIComponent(filter));
      }
    }

    if (cache) {
      urlParts.push('cache=' + encodeURIComponent(cache));
    }

    const urlPartsStr = urlParts.join('&');
    let url = '';
    if (params['isSocial']) {
      url = `/social/projects/${projectId}/collections/${collectionName}/reports/${reportName}?${urlPartsStr}`;
    } else if (collectionName) {
      url = urlFor(
        `/collections/${collectionName}/reports/${reportName}.${format}?${urlPartsStr}`,
        projectId
      );
    } else {
      url = urlFor(
        `/analysis-groups/${analysisGroupId}/reports/${reportName}.${format}?${urlPartsStr}`,
        projectId
      );
    }

    return url;
  }

  getReport<T = { [key: string]: any }>(
    collectionName: string,
    analysisGroupId: string,
    reportName: string,
    params: { [key: string]: any },
    format: 'json' | 'csv',
    query?: string,
    filter?: string,
    cache?: string,
    projectId?: string
  ): Promise<Report<T> | string> {
    const url = this.getReportPermalink(
      collectionName,
      analysisGroupId,
      reportName,
      params,
      query,
      filter,
      cache,
      format,
      projectId,
      true
    );

    const body = { params: remapParamsAsObject(params), query, filter };
    if (format === 'json') {
      return this.post<Report<T>>(url, body);
    } else {
      return this.postText(url, body);
    }
  }

  postReport<T = { [key: string]: any }>(
    collectionName: string,
    analysisGroupId: string,
    reportName: string,
    params: { [key: string]: any },
    body: BodyParams,
    format: 'json' | 'csv',
    query?: string,
    filter?: string,
    cache?: string,
    projectId?: string
  ): Promise<Report<T> | string> {
    const url = this.getReportPermalink(
      collectionName,
      analysisGroupId,
      reportName,
      params,
      query,
      filter,
      cache,
      format,
      projectId
    );

    if (format === 'json') {
      return this.post<Report<T>>(url, body);
    } else {
      return this.postText(url, body);
    }
  }

  getFieldStatistics(
    collectionName: string,
    analysisGroupId: string,
    projectId: string,
    customFields = '*',
    standardFields = '*'
  ): Promise<Report<FieldStatisticsReport>> {
    const url = this.getReportPermalink(
      collectionName,
      analysisGroupId,
      'fieldStatistics',
      { customFields, standardFields },
      undefined,
      undefined,
      undefined,
      'json',
      projectId
    );

    return this.get<Report<FieldStatisticsReport>>(url);
  }

  getTaxonomyNodesReport(
    ds: WidgetDataSource,
    projectId: string,
    query: string,
    timezone: string,
    selectedSections?: SelectedSection[]
  ): Promise<Report<TaxonomyReport>> {
    const params: TaxonomyNodesReportParams = {
      analysisName: ds.analysisId,
      featureName: 'taxonomy',
      featureSectionFilter:
        FilterUtils.convertSelectedSectionToFeatureSectionFilter(selectedSections),
      isSocial: ds.isSocial,
      timezone,
    };
    const url = this.getReportPermalink(
      ds.collectionName,
      ds.groupId,
      'taxonomyNodeDump',
      params,
      query,
      undefined,
      undefined,
      'json',
      projectId,
      true
    );
    const body: BodyParams = { params: remapParamsAsObject({ ...params }), query };
    return this.post<Report<TaxonomyReport>>(url, body);
  }

  getConfigurationTaxonomyNodesReportUrl(configId: string, configName: string): string {
    const url = new URL(`/config/${configId}/export/taxonomy`, this.getBaseUrl());
    url.searchParams.append('token', this.getToken());
    url.searchParams.append('filename', `${configName}-Taxonomy`);
    return url.toString();
  }
}
